import { FeaturePageLayout } from "@/components/features/FeaturePageLayout";
import { FeatureHero } from "@/components/features/FeatureHero";
import { FeatureSection } from "@/components/features/FeatureSection";
import { FeatureCard } from "@/components/features/FeatureCard";
import { FeatureGrid } from "@/components/features/FeatureGrid";
import { HowItWorksSection } from "@/components/features/HowItWorksSection";
import { ScreenshotSection } from "@/components/features/ScreenshotSection";
import { RelatedFeaturesSection } from "@/components/features/RelatedFeaturesSection";
import { FeatureCTA } from "@/components/features/FeatureCTA";
import {
  BarChart3,
  TrendingUp,
  PieChart,
  Filter,
  Download,
  Clock,
  Users,
  DollarSign,
  Star,
  Heart,
  Mail
} from "lucide-react";

export function AnalyticsPage() {
  const features = [
    {
      icon: BarChart3,
      title: "Real-Time Dashboards",
      description: "Monitor your business performance at a glance with live, customizable dashboards for all key metrics."
    },
    {
      icon: TrendingUp,
      title: "Funnel Analytics",
      description: "Track customer journeys through review, recruitment, and loyalty funnels. Identify drop-off points and optimize conversion."
    },
    {
      icon: PieChart,
      title: "Sankey Diagrams",
      description: "Visualize complex customer flows and relationships with beautiful, interactive Sankey diagrams."
    },
    {
      icon: Users,
      title: "Employee Insights",
      description: "Analyze attendance patterns, labor costs, productivity metrics, and workforce trends over time."
    },
    {
      icon: DollarSign,
      title: "Revenue Tracking",
      description: "Monitor the business impact of loyalty programs, campaigns, and customer engagement initiatives."
    },
    {
      icon: Filter,
      title: "Advanced Filtering",
      description: "Drill down into data with powerful filters. Segment by date range, location, customer type, and more."
    },
    {
      icon: Download,
      title: "Export & Reporting",
      description: "Download reports as PDF or Excel. Schedule automated reports to be emailed to stakeholders."
    },
    {
      icon: Clock,
      title: "Historical Analysis",
      description: "Compare performance across time periods. Identify seasonal trends and year-over-year growth."
    }
  ];

  const howItWorksSteps = [
    {
      number: 1,
      title: "Automatic Data Collection",
      description: "All your business activities are tracked automatically across features",
      icon: BarChart3
    },
    {
      number: 2,
      title: "View Dashboards",
      description: "Access pre-built dashboards or create custom views for your needs",
      icon: TrendingUp
    },
    {
      number: 3,
      title: "Analyze Trends",
      description: "Use filters and time ranges to spot patterns and opportunities",
      icon: Filter
    },
    {
      number: 4,
      title: "Take Action",
      description: "Make data-driven decisions to grow your business",
      icon: PieChart
    }
  ];

  const relatedFeatures = [
    {
      title: "Reviews & Feedback",
      description: "Track review sentiment trends and response rates",
      icon: Star,
      link: "/features/reviews"
    },
    {
      title: "Loyalty & Rewards",
      description: "Measure loyalty program ROI and redemption patterns",
      icon: Heart,
      link: "/features/loyalty"
    },
    {
      title: "Marketing & Communications",
      description: "Analyze email campaign performance and engagement",
      icon: Mail,
      link: "/features/marketing"
    }
  ];

  return (
    <FeaturePageLayout>
      <FeatureHero
        title="Make Smarter Decisions with Data & Analytics"
        subtitle="Comprehensive business intelligence across all your operations. Track performance, understand trends, and optimize your strategy with powerful analytics."
        primaryCTA={{ text: "Explore Dashboards", link: "/sign-up" }}
        secondaryCTA={{ text: "See Examples", link: "#how-it-works" }}
      />

      <FeatureSection
        title="Powerful Analytics Tools"
        subtitle="Everything you need to understand and grow your business"
      >
        <FeatureGrid columns={4}>
          {features.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </FeatureGrid>
      </FeatureSection>

      <FeatureSection
        title="How It Works"
        subtitle="Turn data into actionable insights"
        gradient
      >
        <HowItWorksSection steps={howItWorksSteps} />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="See the Complete Picture"
          description="Our unified analytics platform brings together data from reviews, loyalty programs, employee management, recruitment, and marketing campaigns. View everything in one place or drill down into specific areas. Real-time updates keep you informed about what's happening in your business right now."
        />
      </FeatureSection>

      <FeatureSection>
        <ScreenshotSection
          title="Visualize Customer Journeys"
          description="Sankey diagrams and funnel analytics show exactly how customers move through your business. See where they enter, what actions they take, and where they might drop off. Use these insights to optimize every touchpoint and increase conversion rates across all your features."
          imagePosition="left"
        />
      </FeatureSection>

      <FeatureSection
        title="Related Features"
        subtitle="Analytics integrated across all features"
        gradient
      >
        <RelatedFeaturesSection features={relatedFeatures} />
      </FeatureSection>

      <FeatureSection>
        <FeatureCTA
          title="Unlock the Power of Your Business Data"
          description="Stop guessing. Start knowing. Get the insights you need to make confident business decisions."
          primaryCTA={{ text: "View Your Analytics", link: "/sign-up" }}
          secondaryCTA={{ text: "Learn More", link: "#contact" }}
        />
      </FeatureSection>
    </FeaturePageLayout>
  );
}
